/**
 * `PlatformPrinter.resolveEscPosBackend()` on Electron — the generic ESC/POS
 * LAN driver in the main process, seen through the allow-listed bridge.
 *
 * Every method goes through the schema-validated `escPos` surface and
 * surfaces the shell's own typed failure. With no shell the constructor
 * throws `ElectronBridgeUnavailableError`: no bridge means no printing, never
 * a mock (hazard F2). Job outcomes (`not-sent` / `submitted` /
 * `delivery-unknown` / `duplicate`) are passed through untouched so the
 * shared printer service can decide what a retry means.
 */
import { escPosBridge, unwrapBridgeResult } from './bridge';
import type {
  EscPosTarget,
  EscPosLanBackend,
  EscPosUsbPrinter,
  EscPosStatusEvent,
  EscPosDiscoverRequest,
  EscPosDiscoveryProgress,
  EscPosConnectionDescriptor,
} from '../services/types';

/** Drops keys the bridge's `.strict()` schemas would reject, per transport. */
function toConnection(connection: EscPosConnectionDescriptor) {
  const common = {
    name: connection.name,
    identifier: connection.identifier,
    profile: connection.profile as never,
    timeoutMs: connection.timeoutMs,
  };
  switch (connection.transport) {
    case 'windows-raw':
      return {
        transport: 'windows-raw' as const,
        queueName: connection.queueName,
        driverName: connection.driverName,
        portName: connection.portName,
        pnpDeviceId: connection.pnpDeviceId,
        usb: connection.usb,
        ...common,
      };
    case 'serial':
      return {
        transport: 'serial' as const,
        portName: connection.portName,
        baudRate: connection.baudRate,
        friendlyName: connection.friendlyName,
        pnpDeviceId: connection.pnpDeviceId,
        usb: connection.usb,
        ...common,
      };
    case 'lan':
    default:
      return { transport: 'lan' as const, ipAddress: connection.ipAddress, port: connection.port, ...common };
  }
}

function toTarget(target: EscPosTarget) {
  return {
    connectionId: target.connectionId,
    identifier: target.identifier,
    connection: target.connection ? toConnection(target.connection) : undefined,
    timeoutMs: target.timeoutMs,
  };
}

export function createElectronEscPosBackend(): EscPosLanBackend {
  const bridge = escPosBridge();

  return {
    kind: 'escpos-electron-lan',

    async discover(request: EscPosDiscoverRequest) {
      return unwrapBridgeResult(
        'escPos',
        await bridge.discover({
          scanId: request.scanId,
          port: request.port,
          knownAddresses: request.knownAddresses ? [...request.knownAddresses] : undefined,
          sweep: request.sweep,
          interfaceName: request.interfaceName,
          timeoutMs: request.timeoutMs,
          identify: request.identify,
        })
      );
    },

    async cancelDiscovery(scanId: string) {
      return unwrapBridgeResult('escPos', await bridge.cancelDiscovery({ scanId })).cancelled;
    },

    async listInterfaces(port: number) {
      return unwrapBridgeResult('escPos', await bridge.listInterfaces({ port }));
    },

    async listDevices() {
      return unwrapBridgeResult('escPos', await bridge.listDevices({}));
    },

    async watchUsbPrinters(enabled: boolean) {
      return unwrapBridgeResult('escPos', await bridge.watchUsbPrinters({ enabled }));
    },

    async installUsbQueue(pnpDeviceId: string) {
      return unwrapBridgeResult('escPos', await bridge.installUsbQueue({ pnpDeviceId }));
    },

    async setQueuePort(queueName: string, portName: string) {
      return unwrapBridgeResult('escPos', await bridge.setQueuePort({ queueName, portName }));
    },

    async connect(connection) {
      return unwrapBridgeResult('escPos', await bridge.connect(toConnection(connection)));
    },

    async disconnect(target) {
      unwrapBridgeResult('escPos', await bridge.disconnect(toTarget(target)));
    },

    async getStatus(target, options) {
      return unwrapBridgeResult(
        'escPos',
        await bridge.getStatus({ ...toTarget(target), forceReconnect: options?.forceReconnect })
      );
    },

    async printImage(request) {
      return unwrapBridgeResult(
        'escPos',
        await bridge.printImage({
          ...toTarget(request),
          imageBase64: request.imageBase64,
          cutPaper: request.cutPaper,
          openCashDrawer: request.openCashDrawer,
          jobId: request.jobId,
        })
      );
    },

    async printReceipt(request) {
      return unwrapBridgeResult(
        'escPos',
        await bridge.printReceipt({
          ...toTarget(request),
          items: [...request.items],
          cutPaper: request.cutPaper,
          openCashDrawer: request.openCashDrawer,
          copies: request.copies,
          jobId: request.jobId,
        })
      );
    },

    async openCashDrawer(request) {
      return unwrapBridgeResult(
        'escPos',
        await bridge.openCashDrawer({ ...toTarget(request), jobId: request.jobId })
      );
    },

    onStatus(listener: (event: EscPosStatusEvent) => void) {
      return bridge.onStatus((event) => listener(event));
    },

    onDiscoveryProgress(listener: (event: EscPosDiscoveryProgress) => void) {
      return bridge.onDiscoveryProgress((event) => listener(event));
    },

    onUsbPrintersChanged(listener: (printers: readonly EscPosUsbPrinter[]) => void) {
      return bridge.onUsbPrintersChanged((event) => listener(event.printers));
    },
  };
}
